import { useState } from 'react';
import { Pencil, Plus, X, Check } from 'lucide-react';
import toast from 'react-hot-toast';
import API from '../api/axios';

const EMPTY_CATEGORY = { name: '', description: '' };

export default function CategoryManager({ categories, onChanged }) {
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_CATEGORY);
  const [saving, setSaving] = useState(false);

  const startEdit = category => {
    setEditingId(category.id);
    setForm({ name: category.name, description: category.description || '' });
  };

  const reset = () => {
    setEditingId(null);
    setForm(EMPTY_CATEGORY);
  };

  const save = async event => {
    event.preventDefault();
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      if (editingId) await API.put(`/products/categories/${editingId}`, form);
      else await API.post('/products/categories', form);
      toast.success(editingId ? 'Category updated' : 'Category created');
      reset();
      await onChanged();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not save category');
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = {
    width: '100%', padding: '0.65rem 0.8rem', border: '1px solid var(--light-gray)',
    borderRadius: '6px', background: 'var(--white)', font: 'inherit',
  };

  return (
    <section>
      <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.25rem' }}>Categories</h2>
      <p style={{ color: 'var(--mid-gray)', fontSize: '0.88rem', margin: '0.25rem 0 1rem' }}>
        Add or rename categories. Existing products keep their category assignment.
      </p>
      <form onSubmit={save} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.75rem', padding: '1rem', border: '1px solid var(--light-gray)', borderRadius: '8px', marginBottom: '1rem' }}>
        <label>Category name<input required maxLength={100} value={form.name} onChange={event => setForm(current => ({ ...current, name: event.target.value }))} style={inputStyle} /></label>
        <label>Description<textarea maxLength={1000} rows={2} value={form.description} onChange={event => setForm(current => ({ ...current, description: event.target.value }))} style={inputStyle} /></label>
        <div style={{ display: 'flex', alignItems: 'end', gap: '0.5rem' }}>
          <button type="submit" disabled={saving} style={{ ...inputStyle, width: 'auto', display: 'inline-flex', alignItems: 'center', gap: '0.4rem', background: 'var(--charcoal)', color: 'var(--white)', cursor: 'pointer' }}>
            {editingId ? <Check size={15} /> : <Plus size={15} />}{saving ? 'Saving...' : editingId ? 'Save changes' : 'Add category'}
          </button>
          {editingId && <button type="button" onClick={reset} aria-label="Cancel edit" style={{ ...inputStyle, width: 'auto', cursor: 'pointer' }}><X size={16} /></button>}
        </div>
      </form>
      <div style={{ display: 'grid', gap: '0.5rem' }}>
        {categories.map(category => (
          <div key={category.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', padding: '0.8rem 0', borderBottom: '1px solid var(--light-gray)' }}>
            <div><strong>{category.name}</strong>{category.description && <div style={{ color: 'var(--mid-gray)', fontSize: '0.84rem' }}>{category.description}</div>}</div>
            <button type="button" onClick={() => startEdit(category)} style={{ ...inputStyle, width: 'auto', display: 'inline-flex', alignItems: 'center', gap: '0.35rem', cursor: 'pointer' }}><Pencil size={14} /> Edit</button>
          </div>
        ))}
        {!categories.length && <p style={{ color: 'var(--mid-gray)' }}>No categories yet.</p>}
      </div>
    </section>
  );
}
